'use client';

import { MarketplaceFilters } from '@/components/common/navigation/sidebar/components/marketplace-filters';
import { UserProfile } from '@/components/common/navigation/sidebar/components/user-profile';
import Button from '@/components/global/button';
import Typography from '@/components/global/typography';
import { profileService } from '@/lib/services/profile.service';
import { cn } from '@/lib/utils';
import { useI18n } from '@/locales/client';
import { useQuery } from '@tanstack/react-query';
import { SearchIcon } from 'lucide-react';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import { useQueryState } from 'nuqs';
import { memo } from 'react';

const MemoizedUserProfile = memo(UserProfile);

const initialFilters = {
  areasOfLaw: '',
  city: '',
  stateProvince: '',
  country: '',
  language: '',
  page: '1',
};

function MarketplaceSidebar() {
  const t = useI18n();
  const { data: session } = useSession();

  const [filters, setFilters] = useQueryState('filters', {
    defaultValue: initialFilters,
    parse: (value) => {
      try {
        return JSON.parse(decodeURIComponent(value));
      } catch (e) {
        return initialFilters;
      }
    },
    serialize: (value) => encodeURIComponent(JSON.stringify(value)),
  });

  const { data: profileFiltersValues } = useQuery({
    queryKey: ['profileFiltersValues'],
    queryFn: () => profileService.getProfileFiltersValues(session),
    enabled: !!session,
  });

  if (!profileFiltersValues) return null;

  return (
    <div
      className={cn(
        'border-gray-[#E5E7EB] flex h-[calc(100vh-72px)] w-full flex-col border-r bg-white dark:border-gray-700 dark:bg-gray-900'
      )}
    >
      <div className='flex h-full flex-col'>
        <div className='flex-1 space-y-6 overflow-y-auto p-6'>
          <div className='flex flex-col gap-2'>
            <Typography
              size='h5'
              weight='bold'
              className='flex items-center gap-2'
            >
              <Image
                src='/images/icons/star-icon.svg'
                alt='logo'
                width={24}
                height={24}
              />
              {t('marketplace.sidebar.title')}
            </Typography>
            <Typography size='span' color='secondary'>
              {t('marketplace.sidebar.description')}
            </Typography>
          </div>
          <MarketplaceFilters
            profileFiltersValues={profileFiltersValues}
            filters={filters}
            setFilters={setFilters}
          />
          <Button
            size='sm'
            className='w-full gap-2'
            onClick={() => setFilters(initialFilters)}
          >
            <SearchIcon className='size-4' />
            {t('marketplace.sidebar.resetFilters')}
          </Button>
        </div>

        <div className='shrink-0 border-t border-gray-200 p-6 dark:border-gray-700'>
          <MemoizedUserProfile />
        </div>
      </div>
    </div>
  );
}

export default memo(MarketplaceSidebar);
